import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Download, FileText, User, Users, Calendar,
  Mail, Phone, MapPin, CreditCard, CheckCircle, XCircle, Clock, Euro, FileImage, Archive
} from 'lucide-react';
import api, { inscriptionsAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import AdminLayout from './AdminLayout';

const STATUS_BADGES = {
  pending: { label: 'En attente', cls: 'bg-yellow-100 text-yellow-800', icon: Clock },
  validated: { label: 'Validée', cls: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  paid: { label: 'Payée', cls: 'bg-green-100 text-green-800', icon: CheckCircle },
  cancelled: { label: 'Annulée', cls: 'bg-red-100 text-red-800', icon: XCircle },
};

const Section = ({ title, icon: Icon, children }) => (
  <div className="bg-white rounded-xl shadow-md p-6">
    <h3 className="text-lg font-semibold text-[#2C3C65] mb-4 flex items-center">
      {Icon && <Icon className="h-5 w-5 mr-2 text-[#285BBB]" />}
      {title}
    </h3>
    {children}
  </div>
);

const Field = ({ label, value }) => (
  <div>
    <p className="text-xs uppercase tracking-wide text-[#6777A1]">{label}</p>
    <p className="text-sm text-[#2C3C65] font-medium">{value || '—'}</p>
  </div>
);

const AdminInscriptionDetail = () => {
  const { id } = useParams();
  const { toast } = useToast();
  const [inscription, setInscription] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await inscriptionsAPI.get(id);
      setInscription(res.data);
      const ids = res.data?.documents_ids || [];
      const metas = await Promise.all(
        ids.map((fid) => api.get(`/files/${fid}`).then((r) => r.data).catch(() => null))
      );
      setDocs(metas.filter(Boolean));
    } catch (error) {
      toast({ title: 'Erreur', description: "Inscription introuvable", variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (status) => {
    try {
      const res = await inscriptionsAPI.updateStatus(id, status);
      const data = res?.data || res || {};
      const accounts = data.accounts;
      if (accounts && accounts.parents && accounts.parents.length) {
        const created = accounts.parents.filter(p => p.action === 'created').length;
        const reinvited = accounts.parents.filter(p => p.action === 'reinvited').length;
        const existing = accounts.parents.filter(p => p.action === 'existing').length;
        const parts = [];
        if (created) parts.push(`${created} compte parent créé`);
        if (reinvited) parts.push(`${reinvited} invitation renvoyée`);
        if (existing) parts.push(`${existing} compte déjà actif`);
        toast({
          title: 'Statut mis à jour',
          description: parts.length ? `📧 ${parts.join(' · ')}. Email d'invitation envoyé.` : 'Statut changé.',
        });
      } else {
        toast({ title: 'Statut mis à jour' });
      }
      load();
    } catch {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const resendInvitation = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/inscriptions/${id}/create-accounts`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (r.ok && d.ok) {
        const cnt = (d.summary?.parents || []).length;
        toast({ title: 'Invitation(s) envoyée(s)', description: `${cnt} email(s) d'invitation parent envoyé(s).` });
        load();
      } else {
        toast({ title: 'Échec', description: d.detail || 'Erreur', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Erreur réseau', description: String(e), variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#285BBB]" />
        </div>
      </AdminLayout>
    );
  }

  if (!inscription) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-[#6777A1]">Inscription introuvable</p>
          <Link to="/admin/inscriptions" className="text-[#285BBB] hover:underline mt-3 inline-block">
            ← Retour à la liste
          </Link>
        </div>
      </AdminLayout>
    );
  }

  const Badge = STATUS_BADGES[inscription.status] || STATUS_BADGES.pending;
  const BadgeIcon = Badge.icon;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-inscription-detail">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <Link to="/admin/inscriptions" className="text-sm text-[#6777A1] hover:text-[#285BBB] inline-flex items-center mb-2">
              <ArrowLeft className="h-4 w-4 mr-1" /> Retour à la liste
            </Link>
            <h1 className="text-2xl font-bold text-[#2C3C65]">Inscription {inscription.numero}</h1>
            <p className="text-sm text-[#6777A1]">
              Reçue le {new Date(inscription.created_at).toLocaleDateString('fr-FR')}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${Badge.cls}`}>
              <BadgeIcon className="h-3 w-3 mr-1" /> {Badge.label}
            </span>
            <a
              href={inscriptionsAPI.getPDF(inscription.id)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-[#285BBB] hover:bg-[#2C3C65] text-white rounded-lg text-sm font-medium"
              data-testid="download-pdf"
            >
              <Download className="h-4 w-4 mr-2" /> PDF récapitulatif
            </a>
            <button
              type="button"
              onClick={async () => {
                try {
                  toast({ title: 'Préparation du ZIP', description: `Inscription ${inscription.numero || ''}…` });
                  await inscriptionsAPI.downloadZip(inscription.id, inscription.numero);
                } catch (e) {
                  toast({
                    title: 'Téléchargement impossible',
                    description: e?.response?.status === 403 ? 'Accès réservé aux administrateurs.' : 'Erreur lors de la génération du ZIP.',
                    variant: 'destructive',
                  });
                }
              }}
              className="inline-flex items-center px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium"
              data-testid="download-zip"
            >
              <Archive className="h-4 w-4 mr-2" /> ZIP complet (PDF + pièces)
            </button>
          </div>
        </div>

        {/* Status quick-actions */}
        <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap gap-2">
          <span className="text-sm text-[#6777A1] mr-2 self-center">Changer le statut :</span>
          {['pending', 'validated', 'paid', 'cancelled'].map((s) => (
            <Button
              key={s}
              size="sm"
              variant={inscription.status === s ? 'default' : 'outline'}
              onClick={() => updateStatus(s)}
              className={inscription.status === s ? 'bg-[#285BBB] text-white' : ''}
            >
              {STATUS_BADGES[s].label}
            </Button>
          ))}
          {/* Accounts creation status + manual resend */}
          <div className="ml-auto flex items-center gap-3">
            {inscription.accounts_created ? (
              <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1" data-testid="accounts-status-created">
                ✓ Comptes parents créés
              </span>
            ) : (
              <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1" data-testid="accounts-status-pending">
                Comptes parents non créés
              </span>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={resendInvitation}
              data-testid="resend-invitation-btn"
            >
              📧 (Re)envoyer invitation parent
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Enfants */}
          <Section title={`Enfant${inscription.enfants?.length > 1 ? 's' : ''} (${inscription.enfants?.length || 0})`} icon={User}>
            <div className="space-y-4">
              {inscription.enfants?.map((e, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 bg-[#F5F7FF] rounded-lg"
                >
                  <p className="font-semibold text-[#285BBB] mb-2">Enfant {i + 1}</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Prénom" value={e.prenom} />
                    <Field label="Nom" value={e.nom} />
                    <Field label="Sexe" value={e.sexe} />
                    <Field label="Date de naissance" value={e.date_naissance} />
                    <Field label="Nationalité" value={e.nationalite} />
                    <Field label="Établissement" value={e.etablissement_actuel} />
                    <Field label="Classe" value={e.classe} />
                  </div>
                </motion.div>
              ))}
            </div>
          </Section>

          {/* Parents */}
          <Section title="Parents" icon={Users}>
            <div className="space-y-4">
              {['pere', 'mere'].map((key) => {
                const p = inscription[key];
                if (!p || !p.nom) return null;
                return (
                  <div key={key} className="p-4 bg-[#F5F7FF] rounded-lg">
                    <p className="font-semibold text-[#285BBB] capitalize mb-2">{key === 'pere' ? 'Père' : 'Mère'}</p>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Prénom" value={p.prenom} />
                      <Field label="Nom" value={p.nom} />
                      <Field label="Téléphone 1" value={p.telephone1} />
                      <Field label="Téléphone 2" value={p.telephone2} />
                      <Field label="Email" value={p.email} />
                    </div>
                  </div>
                );
              })}
              <div className="p-4 bg-[#F5F7FF] rounded-lg flex items-start gap-3">
                <MapPin className="h-5 w-5 text-[#285BBB] mt-0.5" />
                <div>
                  <p className="text-xs uppercase text-[#6777A1]">Adresse</p>
                  <p className="text-sm text-[#2C3C65]">{inscription.adresse || '—'}</p>
                </div>
              </div>
            </div>
          </Section>

          {/* Documents */}
          <Section title="Documents joints" icon={FileText}>
            {docs.length === 0 ? (
              <p className="text-sm text-[#6777A1]">Aucun document joint.</p>
            ) : (
              <div className="space-y-2">
                {docs.map((d) => {
                  const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api/files/${d.id}/raw`;
                  const isImage = (d.content_type || '').startsWith('image/');
                  return (
                    <div key={d.id} className="flex items-center gap-3 p-3 bg-[#F5F7FF] rounded-lg">
                      <div className="w-10 h-10 bg-white rounded flex items-center justify-center border">
                        {isImage ? <FileImage className="h-5 w-5 text-[#285BBB]" /> : <FileText className="h-5 w-5 text-[#285BBB]" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#2C3C65] truncate">{d.filename}</p>
                        <p className="text-xs text-[#6777A1]">
                          {(d.content_type || '').split('/')[1]?.toUpperCase() || 'FICHIER'} · {((d.size || 0) / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      <a
                        href={apiBase}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1.5 text-xs bg-[#285BBB] hover:bg-[#2C3C65] text-white rounded-md"
                        data-testid={`download-doc-${d.id}`}
                      >
                        <Download className="h-3 w-3 mr-1" /> Télécharger
                      </a>
                    </div>
                  );
                })}
              </div>
            )}
          </Section>

          {/* Cours et tarifs */}
          <Section title="Cours et facturation" icon={CreditCard}>
            <div className="space-y-2">
              {inscription.cours_choisis?.map((c, i) => (
                <div key={i} className="flex justify-between items-center p-3 bg-[#F5F7FF] rounded-lg">
                  <div>
                    <p className="font-medium text-[#2C3C65]">{c.groupe}</p>
                    <p className="text-xs text-[#6777A1]">{c.horaire} · Enfant {c.numero_enfant}</p>
                  </div>
                  <p className="text-sm font-semibold text-[#285BBB]">{c.tarif} €</p>
                </div>
              ))}
              <div className="flex justify-between items-center p-3 bg-[#285BBB] text-white rounded-lg mt-3">
                <span className="font-semibold flex items-center"><Euro className="h-4 w-4 mr-2" /> Total</span>
                <span className="text-lg font-bold">{inscription.total} €</span>
              </div>
            </div>
          </Section>

          {/* Autorisations & Signature */}
          <Section title="Autorisations" icon={CheckCircle}>
            <div className="space-y-3 text-sm">
              <Field label="Autorisation photo" value={inscription.autorisation_photo ? 'Oui' : 'Non'} />
              <Field label="CGV acceptées" value={inscription.cgv_acceptees ? 'Oui' : 'Non'} />
              <Field label="Allergies" value={inscription.allergies} />
              {inscription.personnes_autorisees?.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs uppercase text-[#6777A1] mb-1">Personnes autorisées</p>
                  <ul className="text-sm text-[#2C3C65] list-disc list-inside">
                    {inscription.personnes_autorisees.map((p, i) => (
                      <li key={i}>{p.prenom} {p.nom} ({p.qualite}) — {p.telephone}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </Section>

          <Section title="Signature" icon={Calendar}>
            <Field label="Lieu" value={inscription.lieu_signature} />
            <Field label="Date" value={inscription.date_signature} />
            {inscription.signature_image && (
              <div className="mt-3 p-2 bg-white border rounded-lg">
                <img src={inscription.signature_image} alt="signature" className="max-h-24 object-contain" />
              </div>
            )}
          </Section>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminInscriptionDetail;
